// dashboardApi — typed fetchers for the /v1/dashboard/* family.
// Cookie auth (nexus_session) so callers must include `credentials: "include"`.

import { apiFetch } from "../../lib/api-client.js";

export type SiteHealth = "healthy" | "warning" | "critical";
export type Severity = "info" | "low" | "med" | "high" | "critical";
export type IncidentStatus = "open" | "ack" | "resolved";
export type ActivityKind = "tap" | "incident" | "report" | "user";

export interface OverviewKpi {
  completion: { value: number; suffix: "%"; trend: number; label: string };
  missed: { value: number; suffix: " 次"; trend: number; label: string };
  incidents: { value: number; suffix: " 件"; trend: number; label: string };
  officers: { value: number; suffix: " 人"; trend: number; label: string };
}

export interface OverviewHourlyPoint {
  t: string;
  completion: number;
  missed: number;
}

export interface OverviewWatchSite {
  id: string;
  name: string;
  client: string;
  health: SiteHealth;
  completion: number;
  active_officers: number;
  last_tap: string | null;
}

export interface OverviewIncident {
  id: string;
  ts: string;
  site: string;
  location: string;
  severity: Severity;
  status: IncidentStatus;
  title: string;
  body: string;
}

export interface OverviewActivity {
  id: string;
  ts: string;
  kind: ActivityKind;
  message: string;
  site?: string | null;
}

export interface OverviewResponse {
  tenant_id: string;
  date_local: string;
  kpi: OverviewKpi;
  hourly_series: OverviewHourlyPoint[];
  watch_sites: OverviewWatchSite[];
  recent_incidents: OverviewIncident[];
  activity: OverviewActivity[];
  cached: boolean;
  correlation_id: string;
}

export interface OverviewParams {
  tenantId?: string | null;
  date?: string | null; // YYYY-MM-DD
}

export class DashboardApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

/** Friendly zh-TW message for any DashboardApiError or unknown failure. */
export function friendlyDashboardError(err: unknown): string {
  if (err instanceof DashboardApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色看不到這份資料。請聯絡管理員調整權限。";
    if (err.status === 400 && err.code === "tenant_id_required")
      return "尚未選定操作的單位。請先到「平台 / 租戶」選擇要進入的單位。";
    if (err.status === 500) return "伺服器忙碌中，請稍後重試一次。";
    return "資料暫時無法載入，請稍後再試。";
  }
  return "連線中斷，請檢查網路後再試。";
}

export async function fetchOverview(params: OverviewParams = {}): Promise<OverviewResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.date) qs.set("date", params.date);
  const path = `/v1/dashboard/overview${qs.toString() ? `?${qs}` : ""}`;
  const r = await apiFetch(path, { credentials: "include" });
  if (!r.ok) {
    let code = "request_failed";
    let cid: string | null = r.headers.get("x-correlation-id");
    try {
      const body = (await r.json()) as { error?: string; correlation_id?: string };
      code = body.error ?? code;
      cid = body.correlation_id ?? cid;
    } catch {
      // ignore body parse errors
    }
    throw new DashboardApiError(r.status, code, cid);
  }
  return (await r.json()) as OverviewResponse;
}
